import type { QnaRow } from '../lib/types';
import { ScorePill } from './ScorePill';
import { FeedbackButton } from './FeedbackButton';
import styles from './QnaTable.module.css';

interface Props {
  rows: QnaRow[];
  resultSlug: string;
  className?: string;
}

const flagStyles: Record<QnaRow['flags'][number], string> = {
  'Non-Answer': styles.flagRed,
  Deflection: styles.flagWarn,
  Hedged: styles.flagWarn,
  Confident: styles.flagInfo,
};

function FlagPill({ flag }: { flag: QnaRow['flags'][number] }) {
  return <span className={[styles.flag, flagStyles[flag]].filter(Boolean).join(' ')}>{flag}</span>;
}

const pressLevelLabel = {
  mild: 'Pressed once',
  firm: 'Pressed twice',
  sharp: 'Pressed hard',
} as const;

const pressLevelClass: Record<'mild' | 'firm' | 'sharp', string> = {
  mild: styles.pressMild,
  firm: styles.pressFirm,
  sharp: styles.pressSharp,
};

export function QnaTable({ rows, resultSlug, className }: Props) {
  return (
    <div className={[styles.cards, className].filter(Boolean).join(' ')}>
      {rows.map((r) => (
        <article key={r.id} className={styles.card}>
          <header className={styles.cardHead}>
            <div className={styles.identity}>
              <div className={styles.identityTop}>
                <span className={styles.analyst}>{r.analyst}</span>
                {r.analystKind && <span className={styles.analystKind}>{r.analystKind.replace('-', ' ')}</span>}
              </div>
              <span className={styles.firm}>{r.firm}</span>
            </div>
            <div className={styles.headRight}>
              <ScorePill verdict={r.verdict} size="sm" />
            </div>
          </header>

          <div className={styles.qaBody}>
            <div className={styles.qBlock}>
              <span className={styles.qLabel}>Question</span>
              <p className={styles.qText}>{r.question}</p>
            </div>
            <div className={styles.aBlock}>
              <div className={styles.aHeader}>
                <span className={styles.qLabel}>Answer</span>
                {r.respondedBy && <span className={styles.respondedBy}>by {r.respondedBy}</span>}
              </div>
              <p className={styles.aText}>{r.answerSummary}</p>
              <em className={styles.insight}>{r.insight}</em>
            </div>
          </div>

          {r.followUps && r.followUps.length > 0 && (
            <div className={styles.followUps}>
              <span className={styles.followUpEyebrow}>Follow-up chain</span>
              <ol className={styles.followUpList}>
                {r.followUps.map((fu, i) => (
                  <li key={i} className={styles.followUpRow}>
                    <span className={[styles.pressTag, pressLevelClass[fu.pressLevel]].join(' ')}>
                      {pressLevelLabel[fu.pressLevel]}
                    </span>
                    <p className={styles.followUpQ}>“{fu.question}”</p>
                    <p className={styles.followUpA}>{fu.answerSummary}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <footer className={styles.cardFooter}>
            <ul className={styles.flagList}>
              {r.flags.length === 0 ? (
                <li><span className={styles.noFlag}>No flags</span></li>
              ) : (
                r.flags.map((f) => <li key={f}><FlagPill flag={f} /></li>)
              )}
            </ul>
            <FeedbackButton scope={`${resultSlug}-qna-${r.id}`} label="Was this flag accurate?" />
          </footer>
        </article>
      ))}
    </div>
  );
}
